import React, { useEffect, useState } from "react";
//import axios from "axios";
import axios from 'utils/axiosInstance';

import MemberCheck from "../../components/organs/membership/MemberCheck";
import PaymentMethodSelection from "../../components/organs/membership/PaymentMethodSelection";
import getRootData from 'utils/getRootData';
import { UserInfo } from 'types/user_info';

const VITE_BOOTPAY_ENABLED = import.meta.env.VITE_BOOTPAY_ENABLED === 'true' || import.meta.env.VITE_BOOTPAY_ENABLED === '1';

interface RootData {
  userInfo: UserInfo;
}

const plans = [
  {
    plan_key: "basic",
    title: "준회원",
    price: "무료",
    priceNote: "/월",
    recommended: false,
    level: 1, // 플랜 등급 추가
    features: [
      "회비 무료",
      "황금노트 글 (하루 열람 가능 글 제한 있음)",
      "매달 황금독서클럽",
      "뉴스레터",
      "E-Book",
      "독서클럽 지역모임 및 일부 무료콘텐츠 제공",
      //   "기본 기능 제공",
      //   { text: "고급 분석 기능 제외", excluded: true }
    ],
    button: { label: "선택", style: "outline" }
  },
  {
    plan_key: "silver",
    title: "실버회원",
    price: "₩10,000",
    priceNote: "/월",
    recommended: true,
    level: 2, // 플랜 등급 추가
    features: [
      "모든 준회원 혜택",
      "월 회비 1만원",
      "황금노트 글 (제한없이 열람 가능)",
      "매달 황금독서클럽 뉴스레터 우편발송 & E-Book 제공",
      "독서클럽 지역모임 및 온라인 클래스 참석",
      "역사문화 현장답사",
    ],
    button: { label: "선택", style: "primary" }
  },
  {
    plan_key: "gold",
    title: "골드회원",
    price: "₩10,000",
    priceNote: "/월 이상",
    recommended: false,
    level: 3, // 플랜 등급 추가
    features: [
      "모든 실버회원 혜택",
      "월 회비 1만원 이상",
      "골드회원 특별 모임",
      "명사초청특강『도전道典』 특강"
    ],
    button: { label: "선택", style: "outline" }
  },
  // {
  //   plan_key: "special",
  //   title: "특별회원",
  //   price: "특별 후원금",
  //   priceNote: "300만 원 이상",
  //   recommended: false,
  //   level: 4, // 플랜 등급 추가
  //   isSpecial: true, // 가로로 길게 표시되는 특별 플랜 표시
  //   description: "특별 후원금은 CMS 결제만 가능합니다.",
  //   features: [
  //     "모든 정기회원 혜택",
  //     "특별회원 모임 초대",
  //     "저자 명사초청 강연 우선권",
  //     "율려화 빛꽃수행법 전수"
  //   ],
  //   button: { label: "신청하기", style: "primary" }
  // }
];

interface SelectPlanProps {
  userInfo?: UserInfo | null;
  redirect_url?: string;
  member_subscription?: any;
}

export default function SelectPlan(props: SelectPlanProps = {}) {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [memberCheckResult, setMemberCheckResult] = useState(null);
  const [showMemberCheck, setShowMemberCheck] = useState(false);
  const [showPaymentMethodSelection, setShowPaymentMethodSelection] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // 사용자 정보 가져오기
  const { userInfo: rootUserInfo } = getRootData<RootData>() ?? { userInfo: null };
  const userInfo = props.userInfo || rootUserInfo;
  const isLoggedIn = !!userInfo?.id;
  const isAdmin = userInfo?.roleinfo?.isAllAdminGroup ?? userInfo?.roleinfo?.is_all_admin_group;

  //console.log('userInfo', userInfo);

  //useEffect(() => {
    // if(!VITE_BOOTPAY_ENABLED)
    //   alert("현재 결제 시스템이 준비 중입니다. 효성cms로 가입 신청해 주세요.");
  //}, []);

  useEffect(() => {
    // 플랜 가입 여부 확인 API 호출
    const fetchPlanStatus = async () => {
      try {
        const res = await axios.get("/api/v1/plans/status");
        if (res.data.status === "subscribed") {
          //console.log("가입된 플랜:", res.data.membership);
          // 이미 가입된 경우 my-plan으로 이동
          //navigate("/my_plan");
          setCurrentPlan(res.data.membership.plan_key);
        } else {
          setCurrentPlan("not_subscribed");
        }
      } catch (err) {
        console.error("플랜 확인 실패", err);
        // 로그인이 안된 경우에도 플랜 선택은 가능하도록
        setCurrentPlan("not_subscribed");
      }
    };
    fetchPlanStatus();

    // 로그인 후 돌아온 경우 이전에 선택했던 플랜 처리
    const savedPlan = sessionStorage.getItem('selectedPlan');
    if (savedPlan && isLoggedIn) {
      sessionStorage.removeItem('selectedPlan');
      // 잠시 후 자동 선택 (상태가 로드된 후)
      setTimeout(() => {
        handleSelect(savedPlan);
      }, 500);
    }
  }, [isLoggedIn]);

  // 현재 플랜의 등급 계산
  const getCurrentPlanLevel = () => {
    if (currentPlan === "not_subscribed") return 0;
    const plan = plans.find(p => p.plan_key === currentPlan);
    return plan ? plan.level : 0;
  };

  // 플랜 선택 가능 여부 확인
  const isPlanSelectable = (planLevel) => {
    const currentLevel = getCurrentPlanLevel();
    return planLevel > currentLevel || // 현재 플랜보다 높은 등급만 선택 가능
        planLevel == 3; // Gold level은 다시 가입가능
  };

  // 회원 체크 결과 처리
  const handleMemberCheckResult = (result) => {
    setMemberCheckResult(result);

    if (result.exists) {
      //alert('해당 이메일/전화번호로 가입하신 이력이 확인됩니다. 해당 계정으로 가입을 게속 진행하겠습니다.')
      // 기존 회원인 경우 - 선택된 member_id를 세션에 저장
      if (result.member?.id) {
        sessionStorage.setItem('selectedMemberId', result.member.id.toString());
      }

      // 결제 방법 선택으로 진행
      setShowMemberCheck(false);

      if (selectedPlan?.plan_key === "basic") {
        // 무료 플랜은 바로 처리
        proceedWithPlanSelection(selectedPlan.plan_key);
      } else {
        // 유료 플랜은 결제 방법 선택 화면으로
        setShowPaymentMethodSelection(true);
      }
    } else {
      // 가입 이력이 없는 경우 - MemberCheck 화면을 유지하여 사용자가 선택할 수 있게 함
      // setShowMemberCheck(false); // 이 줄을 제거하여 MemberCheck 화면을 유지
    }
  };

  const handleSelect = (planKey) => {
    // 로그인하지 않은 경우 로그인 유도
    if (!isLoggedIn) {
      if (confirm('로그인이 필요합니다. 로그인 페이지로 이동하시겠습니까?')) {
        // 현재 페이지 정보를 저장하고 로그인 후 돌아올 수 있도록
        sessionStorage.setItem('selectedPlan', planKey);
        window.location.href = '/users/sign_in';
      }
      return;
    }

    const plan = plans.find(p => p.plan_key === planKey);
    setSelectedPlan(plan);

    // 이미 가입된 플랜이 있으면 회원 체크 없이 바로 진행
    if (currentPlan !== "not_subscribed") {
      if (planKey === "basic") {
        proceedWithPlanSelection(planKey);
      } else {
        setShowPaymentMethodSelection(true);
      }
      return;
    }

    // 이미 user에 member가 연결되어 있으면 회원 체크 건너뛰기
    if (userInfo?.member_id) {
      if (planKey === "basic") {
        proceedWithPlanSelection(planKey);
      } else {
        setShowPaymentMethodSelection(true);
      }
      return;
    }

    // 신규 가입자의 경우 회원 체크를 먼저 진행
    setShowMemberCheck(true);
  };

  const proceedWithPlanSelection = (planKey) => {
    if (planKey === "basic") {
      // 무료회원 선택 시 가입 처리
      axios.post("/api/v1/plans/subscribe", { plan_key: planKey })
        .then(() => {
          alert("무료회원으로 가입되었습니다.");
          window.location.href = "/my_plan";
        })
        .catch(err => {
          console.error("가입 실패", err);
          alert("가입 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
        });
    } else {
      // 유료 플랜은 결제 방법 선택 화면으로 이동
      setShowPaymentMethodSelection(true);
    }
  };

  const handleCardPayment = () => {
    if(VITE_BOOTPAY_ENABLED || isAdmin) {
      window.location.href = `/payment/membership/${selectedPlan.plan_key}`;
    } else {
      alert("현재 카드 결제 시스템이 준비 중입니다.");
    }
  };

  const handleCmsPayment = () => {
    // 효성CMS로 이동
    document.location.href = '/document/membership1';
  };

  if (!currentPlan) {
    return <div className="text-center py-20 text-lg">가입 상태를 확인 중입니다...</div>;
  }

  // MemberCheck 모달이 열려있으면 MemberCheck 컴포넌트 렌더링
  if (showMemberCheck) {
    return (
      <MemberCheck
        user={{
          email: userInfo?.email,
          phone: userInfo?.phone, // member 정보에서 전화번호를 가져올 수 있다면 추가
          name: userInfo?.name // 이름 정보 추가
        }}
        autoCheck={true}
        onResult={(result) => {
          handleMemberCheckResult(result);
          // 체크 완료 후 선택된 플랜으로 진행
          //proceedWithPlanSelection(window.selectedPlanKey);
        }}
        onProceedAsNewMember={() => {
          // 신규 회원의 경우 selectedMemberId 세션 제거
          sessionStorage.removeItem('selectedMemberId');

          setShowMemberCheck(false);
          if (selectedPlan?.plan_key === "basic") {
            proceedWithPlanSelection(selectedPlan.plan_key);
          } else {
            setShowPaymentMethodSelection(true);
          }
        }}
        onBack={() => {
          setShowMemberCheck(false);
          setMemberCheckResult(null);
        }}
      />
    );
  }

  // 결제 방법 선택 화면
  if (showPaymentMethodSelection && selectedPlan) {
    return (
      <PaymentMethodSelection
        planKey={selectedPlan.plan_key}
        planName={selectedPlan.title}
        onSelectCardPayment={handleCardPayment}
        onSelectCmsPayment={handleCmsPayment}
        onBack={() => {
          setShowPaymentMethodSelection(false);
          setSelectedPlan(null);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <div className="container mx-auto px-4 py-16">
        <div className="mx-auto max-w-5xl p-2 md:p-6">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-3">회원 구분 가입</h1>
            <p className="text-lg text-base-content/70 max-w-2xl mx-auto">
              정기회원은 매월 황금독서클럽에서 제공하는 정기적인 온라인 콘텐츠와 오프라인 콘텐츠를 제공하고 준회원은 혜택에 제한이 있습니다. 특별회원에게는 우대서비스를 제공해 드립니다.
            </p>
            {currentPlan !== "not_subscribed" && (
              <div className="mt-6">
                <button
                  onClick={() => window.location.href = "/my_plan"}
                  className="btn btn-outline btn-sm"
                >
                  ← 내 멤버십 현황 보기
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-8 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {plans.filter(p => !p.isSpecial).map((plan) => (
                <div
                  key={plan.plan_key}
                  className={`card bg-base-100 shadow-md transition-all duration-300 border ${plan.recommended
                      ? "border-2 border-primary scale-105 relative shadow-xl"
                      : "border-transparent hover:border-primary/40 hover:shadow-lg"
                    }`}
                >
                {plan.recommended && (
                  <div className="badge badge-primary absolute right-4 -top-3">추천</div>
                )}
                <div className="card-body flex flex-col">
                  <h2 className="card-title text-2xl font-bold">{plan.title}</h2>
                  <div className="my-4 flex items-center justify-center h-[56px]">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    <span className="text-base-content/70 ml-2">{plan.priceNote}</span>
                  </div>
                  <ul className="space-y-2 mb-6 grow">
                    {plan.features.map((feature, idx) =>
                      typeof feature === "string" ? (
                        <li className="flex items-start" key={idx}>
                          <i className="text-success mr-2 mt-1">✔</i> {feature}
                        </li>
                      ) : (
                        <li
                          className={`flex items-start text-base-content/60`} key={idx}
                        >
                          <i className="text-error mr-2 mt-1">✘</i> {feature}
                        </li>
                      )
                    )}
                  </ul>
                  <div className="card-actions mt-auto">
                    {currentPlan === plan.plan_key ? (
                      <div className="w-full text-center">
                        <div className="text-success font-bold mb-2">현재 가입된 플랜입니다</div>
                        <button
                          className="btn btn-outline btn-disabled w-full"
                          disabled
                        >
                          현재 플랜
                        </button>
                      </div>
                    ) : !isPlanSelectable(plan.level) ? (
                      <div className="w-full text-center">
                        <div className="text-base-content/60 text-sm mb-2">현재 플랜보다 낮은 등급은 선택할 수 없습니다</div>
                        <button
                          className="btn btn-outline btn-disabled w-full"
                          disabled
                        >
                          선택 불가
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleSelect(plan.plan_key)}
                        className={`btn btn-${plan.button.style} btn-primary w-full`}
                      >
                        {plan.button.label}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </div>
            {plans.find(p => p.isSpecial) && (
              <div className="w-full">
                {plans.filter(p => p.isSpecial).map((plan) => (
                  <div
                    key={plan.plan_key}
                    className={`card bg-base-100 shadow-md transition-all duration-300 border ${plan.recommended
                        ? "border-2 border-primary scale-105 relative shadow-xl"
                        : "border-transparent hover:border-primary/40 hover:shadow-lg"
                      }`}
                  >
                    {plan.recommended && (
                      <div className="badge badge-primary absolute right-4 -top-3">추천</div>
                    )}
                    <div className="card-body flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex-1">
                        <h2 className="card-title text-2xl font-bold">{plan.title}</h2>
                        <div className="my-4 flex items-center h-[56px]">
                          <span className="text-2xl font-bold">{plan.price}</span>
                          <span className="text-base-content/70 ml-2">{plan.priceNote}</span>
                        </div>
                        {plan.description && (
                          <div className="text-sm text-info mt-2">{plan.description}</div>
                        )}
                      </div>
                      <div className="flex-1">
                        <ul className="space-y-2 mb-6">
                          {plan.features.map((feature, idx) =>
                            typeof feature === "string" ? (
                              <li className="flex items-start" key={idx}>
                                <i className="text-success mr-2 mt-1">✔</i> {feature}
                              </li>
                            ) : (
                              <li
                                className={`flex items-start text-base-content/60`} key={idx}
                              >
                                <i className="text-error mr-2 mt-1">✘</i> {feature}
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                      <div className="flex-shrink-0 md:ml-8">
                        <div className="card-actions">
                          {plan.isSpecial ? (
                            <button
                              onClick={() => window.location.href = '/document/membership1'}
                              className={`btn btn-${plan.button.style} btn-primary`}
                            >
                              {plan.button.label}
                            </button>
                          ) : currentPlan === plan.plan_key ? (
                            <div className="text-center">
                              <div className="text-success font-bold mb-2">현재 가입된 플랜입니다</div>
                              <button
                                className="btn btn-outline btn-disabled w-full"
                                disabled
                              >
                                현재 플랜
                              </button>
                            </div>
                          ) : !isPlanSelectable(plan.level) ? (
                            <div className="text-center">
                              <div className="text-base-content/60 text-sm mb-2">현재 플랜보다 낮은 등급은 선택할 수 없습니다</div>
                              <button
                                className="btn btn-outline btn-disabled w-full"
                                disabled
                              >
                                선택 불가
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleSelect(plan.plan_key)}
                              className={`btn btn-${plan.button.style} btn-primary w-full`}
                            >
                              {plan.button.label}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}